import { normalizeRoutePath } from './pagination';

const normalizeText = (value = '') => `${value || ''}`.trim();

const encodeStrictUriComponent = (value = '') => encodeURIComponent(value)
    .replace(/[!'()*]/g, (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`);

const pickFirstTitle = (...candidates) => {
    for (const candidate of candidates) {
        const normalized = normalizeText(candidate);
        if (normalized) {
            return normalized;
        }
    }

    return '';
};

export const normalizeAnimeTitlePreference = (value = '') => {
    const normalized = normalizeText(value).toLowerCase();
    if (normalized === 'default') {
        return 'romaji';
    }
    if (['english', 'romaji', 'japanese'].includes(normalized)) {
        return normalized;
    }
    return 'english';
};

const getAnimeTitleParts = (anime = {}) => {
    const attributeTitles = anime?.attributes?.titles || {};
    const rootTitles = anime?.titles || {};
    const english = pickFirstTitle(
        rootTitles.en,
        attributeTitles.en,
        anime?.title_english,
        anime?.titleEnglish
    );
    const japanese = pickFirstTitle(
        rootTitles.ja_jp,
        attributeTitles.ja_jp,
        anime?.title_japanese,
        anime?.titleJapanese
    );
    const romaji = pickFirstTitle(
        rootTitles.default,
        attributeTitles.default,
        rootTitles.en_jp,
        attributeTitles.en_jp,
        anime?.attributes?.title
    );
    const fallbackTitle = pickFirstTitle(
        romaji,
        english,
        japanese,
        anime?.attributes?.title,
        anime?.title,
        anime?.name
    );

    return {
        romaji: pickFirstTitle(
            romaji,
            anime?.attributes?.title,
            anime?.title,
            fallbackTitle
        ),
        english,
        japanese,
        fallback: fallbackTitle
    };
};

export const getAnimeDisplayTitle = (anime = {}, preference = 'romaji') => {
    const normalizedPreference = normalizeAnimeTitlePreference(preference);
    const titleParts = getAnimeTitleParts(anime);

    if (normalizedPreference === 'romaji') {
        return pickFirstTitle(
            titleParts.romaji,
            titleParts.english,
            titleParts.japanese,
            titleParts.fallback,
            'Untitled'
        );
    }

    if (normalizedPreference === 'english') {
        return pickFirstTitle(
            titleParts.english,
            titleParts.romaji,
            titleParts.japanese,
            titleParts.fallback,
            'Untitled'
        );
    }

    return pickFirstTitle(
        titleParts.japanese,
        titleParts.romaji,
        titleParts.english,
        titleParts.fallback,
        'Untitled'
    );
};

export const getAnimeTitle = (anime = {}) => {
    const titleParts = getAnimeTitleParts(anime);

    return pickFirstTitle(
        titleParts.romaji,
        titleParts.english,
        titleParts.japanese,
        titleParts.fallback,
        'Untitled'
    );
};

export const toAnimeSlug = (title = '') => encodeStrictUriComponent(normalizeText(title).replace(/\s+/g, '_'));

const normalizeCanonicalTitle = (title = '') => {
    const normalized = normalizeText(title);
    if (!normalized || normalized.toLowerCase() === 'untitled') {
        return '';
    }
    return normalized;
};

export const normalizeAnimeOverviewSection = (value = '') => {
    const normalized = normalizeText(value).toLowerCase();
    if (['details', 'episodes', 'characters', 'staff', 'watch-order', 'themes'].includes(normalized)) {
        return normalized;
    }
    return 'details';
};

export const buildAnimeOverviewPath = ({
    animeId = '',
    title = '',
    section = 'details'
} = {}) => {
    if (!Number.isFinite(Number(animeId))) {
        return '';
    }

    const normalizedSection = normalizeAnimeOverviewSection(section);
    return normalizeRoutePath(`/overview/${animeId}/${toAnimeSlug(title)}/${normalizedSection}`);
};

export const buildCanonicalAnimeOverviewPath = ({
    anime = {},
    animeId = '',
    fallbackTitle = '',
    section = 'details'
} = {}) => {
    const resolvedAnimeId = anime?.id ?? anime?.anime_id ?? anime?.mal_id ?? animeId;
    const resolvedTitle = pickFirstTitle(
        normalizeCanonicalTitle(getAnimeTitle(anime)),
        normalizeCanonicalTitle(`${fallbackTitle || ''}`.replace(/_/g, ' '))
    );

    if (!resolvedTitle) {
        return '';
    }

    return buildAnimeOverviewPath({
        animeId: resolvedAnimeId,
        title: resolvedTitle,
        section
    });
};

export const buildAnimeWatchingPath = ({
    animeId = '',
    title = ''
} = {}) => {
    if (!Number.isFinite(Number(animeId))) {
        return '';
    }

    return normalizeRoutePath(`/watching/${animeId}/${toAnimeSlug(title)}`);
};

export const toAnimeOverviewPath = (anime = {}, section = 'details') => {
    const animeId = anime?.id ?? anime?.anime_id ?? anime?.mal_id;
    if (!Number.isFinite(Number(animeId))) {
        return '';
    }

    return buildAnimeOverviewPath({
        animeId,
        title: getAnimeTitle(anime),
        section
    });
};
